import SEO from "@/components/SEO";
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/pages/home/components/Footer";

interface PublicProfile {
  id: string;
  nome: string;
  sobrenome: string | null;
  avatar_url: string | null;
  bio: string | null;
  instagram: string | null;
  youtube: string | null;
  spotify: string | null;
  tiktok: string | null;
  data_nascimento: string | null;
  mostrar_whatsapp: boolean;
  telefone: string | null;
  email_contato: string | null;
}

export default function ComunidadePage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || "https://example.com";
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profiles, setProfiles] = useState<PublicProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState("");
  const [erro, setErro] = useState("");
  const [activeTab, setActiveTab] = useState<"explorar" | "forum">("explorar");

  const schema = {
    "@context": "https://schema.org",
    "@type": "CommunityPage",
    name: "Comunidade de Músicos Gospel | GodoyProd",
    url: `${siteUrl}/comunidade`,
    description: "Conecte-se com músicos e ministros de louvor na comunidade GodoyProd. Descubra talentos, troque experiências e compartilhe partituras e redes sociais.",
    inLanguage: "pt-BR",
  };

  useEffect(() => {
    carregarPerfis();
  }, []);

  async function carregarPerfis() {
    setLoading(true);
    setErro("");
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, nome, sobrenome, avatar_url, bio, instagram, youtube, spotify, tiktok, data_nascimento, mostrar_whatsapp, telefone, email_contato")
        .eq("perfil_publico", true)
        .order("nome", { ascending: true });

      if (error) {
        console.error("Erro detalhado ao carregar comunidade:", error);
        setErro(`Erro ao carregar comunidade: ${error.message || "Verifique sua conexão e tente novamente."}`);
      } else {
        setProfiles(data || []);
        if ((data || []).length === 0) {
          console.log("Nenhum perfil público encontrado no banco.");
        }
      }
    } catch (unexpectedErr: any) {
      console.error("Erro inesperado:", unexpectedErr);
      setErro(`Erro inesperado: ${unexpectedErr?.message || "Falha na conexão com o servidor."}`);
    } finally {
      setLoading(false);
    }
  }

  function calcularIdade(dataNasc: string | null): number | null {
    if (!dataNasc) return null;
    const nasc = new Date(dataNasc);
    const hoje = new Date();
    let idade = hoje.getFullYear() - nasc.getFullYear();
    const m = hoje.getMonth() - nasc.getMonth();
    if (m < 0 || (m === 0 && hoje.getDate() < nasc.getDate())) idade--;
    return idade;
  }

  const perfisFiltrados = profiles.filter((p) => {
    const termo = busca.toLowerCase();
    return (
      (p.nome || "").toLowerCase().includes(termo) ||
      (p.sobrenome || "").toLowerCase().includes(termo) ||
      (p.bio || "").toLowerCase().includes(termo)
    );
  });

  function hasSocialLinks(p: PublicProfile) {
    return p.instagram || p.youtube || p.spotify || p.tiktok;
  }

  return (
    <>
      <SEO
        title="Comunidade de Músicos Gospel"
        description="Conecte-se com músicos e ministros de louvor na comunidade GodoyProd. Descubra talentos, troque experiências e compartilhe partituras."
        keywords="comunidade músicos gospel, músicos gospel, ministério de louvor, rede de músicos, compartilhar partituras"
        schema={schema}
        canonicalPath="/comunidade"
      />
      <div className="min-h-screen bg-godoy-black">
        <Navbar />

        <section className="pt-32 pb-20 md:pt-40 md:pb-28">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-6xl mx-auto">
            {/* Header */}
            <div className="mb-6">
              <span className="text-godoy-gold text-sm font-semibold tracking-widest uppercase">
                GodoyProd
              </span>
              <h1 className="font-serif text-3xl md:text-4xl font-bold text-godoy-cream mt-3">
                Comunidade
              </h1>
              <p className="text-godoy-lightgray mt-3 max-w-xl">
                Conecte-se com músicos e ministros de louvor. Descubra talentos,
                troque experiências e acompanhe o trabalho de outros artistas da comunidade.
              </p>
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-1 mb-8 border-b border-godoy-gray/20">
              <button
                onClick={() => setActiveTab("explorar")}
                className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${
                  activeTab === "explorar"
                    ? "text-godoy-gold border-godoy-gold"
                    : "text-godoy-lightgray border-transparent hover:text-godoy-cream"
                }`}
              >
                <i className="ri-user-search-line mr-1.5" />
                Explorar Músicos
              </button>
              <button
                onClick={() => navigate("/comunidade/forum")}
                className="px-4 py-3 text-sm font-medium transition-colors border-b-2 border-transparent text-godoy-lightgray hover:text-godoy-cream whitespace-nowrap"
              >
                <i className="ri-community-line mr-1.5" />
                Feed / Fórum
              </button>
            </div>

            {activeTab === "explorar" && (
              <>
                {/* Busca */}
                <div className="mb-8">
                  <div className="relative max-w-lg">
                    <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-godoy-lightgray/50" />
                    <input
                      type="text"
                      value={busca}
                      onChange={(e) => setBusca(e.target.value)}
                      placeholder="Buscar por nome, biografia..."
                      className="w-full pl-11 pr-4 py-3 bg-godoy-charcoal/60 border border-godoy-gray/30 rounded-md text-godoy-cream placeholder:text-godoy-lightgray/40 focus:outline-none focus:border-godoy-gold/50 text-sm"
                    />
                  </div>
                </div>

                {erro && (
                  <div className="mb-6 p-3 bg-red-500/10 border border-red-500/30 rounded-md text-red-400 text-sm">
                    {erro}
                  </div>
                )}

                {!user && (
                  <div className="mb-8 p-4 bg-godoy-charcoal/40 border border-godoy-gray/20 rounded-lg flex items-center justify-between flex-wrap gap-3">
                    <p className="text-godoy-lightgray text-sm">
                      Faca login para aparecer na comunidade e conectar com outros músicos.
                    </p>
                    <Link
                      to="/login?redirect=/comunidade"
                      className="px-5 py-2 bg-godoy-gold text-godoy-black font-semibold rounded-md hover:bg-godoy-goldlight transition-all text-sm whitespace-nowrap"
                    >
                      Entrar
                    </Link>
                  </div>
                )}

                {loading ? (
                  <div className="flex items-center justify-center py-20">
                    <div className="w-8 h-8 border-2 border-godoy-gold border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : perfisFiltrados.length === 0 ? (
                  <div className="text-center py-20 bg-godoy-charcoal/40 border border-godoy-gray/20 rounded-lg">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-godoy-gold/10 flex items-center justify-center">
                      <i className="ri-team-line text-2xl text-godoy-gold" />
                    </div>
                    <h3 className="font-serif text-xl text-godoy-cream mb-2">
                      {busca ? "Nenhum resultado encontrado" : "Nenhum perfil público ainda"}
                    </h3>
                    <p className="text-godoy-lightgray text-sm max-w-md mx-auto">
                      {busca
                        ? "Tente outro termo de busca."
                        : "Os primeiros membros ainda não ativaram seus perfis públicos. Seja um dos pioneiros!"}
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {perfisFiltrados.map((p) => {
                      const idade = calcularIdade(p.data_nascimento);
                      return (
                        <div
                          key={p.id}
                          className="bg-godoy-charcoal/60 border border-godoy-gray/20 rounded-lg p-6 hover:border-godoy-gold/30 transition-all group"
                        >
                          <div className="flex items-start gap-4 mb-4">
                            <div className="w-14 h-14 rounded-full overflow-hidden border border-godoy-gold/20 flex-shrink-0 bg-godoy-charcoal">
                              {p.avatar_url ? (
                                <img src={p.avatar_url} alt={p.nome} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <i className="ri-user-line text-2xl text-godoy-lightgray" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-serif text-base font-semibold text-godoy-cream truncate">
                                {p.nome} {p.sobrenome || ""}
                              </h3>
                              {idade !== null && (
                                <p className="text-godoy-lightgray/60 text-xs mt-0.5">{idade} anos</p>
                              )}
                              {p.bio && (
                                <p className="text-godoy-lightgray/70 text-xs mt-1.5 line-clamp-2">{p.bio}</p>
                              )}
                            </div>
                          </div>

                          {hasSocialLinks(p) && (
                            <div className="flex flex-wrap gap-2 mb-4">
                              {p.instagram && (
                                <a
                                  href={p.instagram}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="w-8 h-8 flex items-center justify-center rounded-md bg-godoy-black/40 text-godoy-lightgray hover:text-godoy-gold hover:bg-godoy-gold/10 transition-all"
                                  title="Instagram"
                                >
                                  <i className="ri-instagram-line text-sm" />
                                </a>
                              )}
                              {p.youtube && (
                                <a
                                  href={p.youtube}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="w-8 h-8 flex items-center justify-center rounded-md bg-godoy-black/40 text-godoy-lightgray hover:text-red-400 hover:bg-red-500/10 transition-all"
                                  title="YouTube"
                                >
                                  <i className="ri-youtube-line text-sm" />
                                </a>
                              )}
                              {p.spotify && (
                                <a
                                  href={p.spotify}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="w-8 h-8 flex items-center justify-center rounded-md bg-godoy-black/40 text-godoy-lightgray hover:text-emerald-400 hover:bg-emerald-500/10 transition-all"
                                  title="Spotify"
                                >
                                  <i className="ri-spotify-line text-sm" />
                                </a>
                              )}
                              {p.tiktok && (
                                <a
                                  href={p.tiktok}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="w-8 h-8 flex items-center justify-center rounded-md bg-godoy-black/40 text-godoy-lightgray hover:text-white hover:bg-black/40 transition-all"
                                  title="TikTok"
                                >
                                  <i className="ri-tiktok-line text-sm" />
                                </a>
                              )}
                              {p.mostrar_whatsapp && p.telefone && (
                                <a
                                  href={`https://wa.me/${p.telefone.replace(/\D/g, "")}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="w-8 h-8 flex items-center justify-center rounded-md bg-godoy-black/40 text-godoy-lightgray hover:text-emerald-400 hover:bg-emerald-500/10 transition-all"
                                  title="WhatsApp"
                                >
                                  <i className="ri-whatsapp-line text-sm" />
                                </a>
                              )}
                            </div>
                          )}

                          <button
                            onClick={() => navigate(`/comunidade/perfil/${p.id}`)}
                            className="w-full py-2.5 bg-godoy-gold/10 border border-godoy-gold/30 text-godoy-gold rounded-md text-sm font-medium hover:bg-godoy-gold/20 transition-all"
                          >
                            Ver Perfil
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}